import React from 'react';
import { motion } from 'framer-motion';
import { Utensils, Eye, Heart } from 'lucide-react';
import { useLang } from '../context/LanguageContext';

const t = {
  fr: {
    label: 'Notre différence',
    title: 'Pourquoi Cocoonut Digital ?',
    subtitle: 'Avant de parler stratégie, regardons ce qu\'il y a dans la recette.',
    features: [
      { icon: Utensils, title: "Spécialiste de l'hospitalité", description: "Nous connaissons le rythme d'un service, les cuisines qui s'animent et les salles pleines un samedi soir." },
      { icon: Eye, title: "Un regard créatif", description: "Nous créons des images et des histoires qui reflètent l'atmosphère réelle d'un lieu." },
      { icon: Heart, title: "Une communication humaine", description: "Pas de communication industrielle : chaque projet est pensé avec sensibilité et précision." },
    ],
  },
  en: {
    label: 'What sets us apart',
    title: 'Why Cocoonut Digital?',
    subtitle: 'Before talking strategy, let\'s look at what\'s in the recipe.',
    features: [
      { icon: Utensils, title: "Hospitality specialist", description: "We know the rhythm of a service, the kitchens coming alive and the dining rooms full on a Saturday night." },
      { icon: Eye, title: "A creative eye", description: "We create images and stories that reflect the true atmosphere of a venue." },
      { icon: Heart, title: "Human communication", description: "No industrial communication: every project is crafted with sensitivity and precision." },
    ],
  },
};

const WhySection = () => {
  const { lang } = useLang();
  const tx = t[lang];

  const containerVariants = { hidden: { opacity: 0 }, visible: { opacity: 1, transition: { staggerChildren: 0.2 } } };
  const itemVariants = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } } };

  return (
    <section className="relative py-24 md:py-32 bg-white overflow-hidden">
      <div className="container mx-auto px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-100px" }} transition={{ duration: 0.6 }} className="mb-6">
            <span className="text-sm uppercase tracking-widest text-[#717171] font-mono">{tx.label}</span>
          </motion.div>

          <motion.h2 initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-100px" }} transition={{ duration: 0.6, delay: 0.1 }} className="text-4xl md:text-5xl lg:text-6xl font-semibold text-[#151515] mb-6 leading-tight">
            {tx.title}
          </motion.h2>

          <motion.p initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-100px" }} transition={{ duration: 0.6, delay: 0.2 }} className="text-lg md:text-xl text-[#151515]/70 mb-16 max-w-2xl">
            {tx.subtitle}
          </motion.p>

          <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-50px" }} className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
            {tx.features.map((feature, index) => (
              <motion.div key={index} variants={itemVariants} className="group">
                <motion.div whileHover={{ scale: 1.05, rotate: 3 }} transition={{ type: "spring", stiffness: 300 }} className="w-14 h-14 rounded-2xl bg-[#f5e6d3] flex items-center justify-center mb-6 group-hover:bg-[#d4a574] transition-colors duration-300">
                  <feature.icon className="w-7 h-7 text-[#1a3a52] group-hover:text-white transition-colors duration-300" />
                </motion.div>
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <span className="text-[#d4a574] font-bold">✓</span>
                    <h3 className="text-xl font-semibold text-[#151515]">{feature.title}</h3>
                  </div>
                  <p className="text-[#151515]/70 leading-relaxed">{feature.description}</p>
                </div>
                <motion.div initial={{ scaleX: 0 }} whileInView={{ scaleX: 1 }} viewport={{ once: true }} transition={{ duration: 0.8, delay: 0.3 + index * 0.1 }} className="h-px bg-gradient-to-r from-[#d4a574] to-transparent mt-6 origin-left" />
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>
      <div className="absolute bottom-0 left-0 w-64 h-64 bg-[#f5e6d3]/30 rounded-full blur-3xl pointer-events-none" />
    </section>
  );
};

export default WhySection;
